import { MAX_DISTANCE_VALUE } from "constants/configConstants";

export type FiltersAPIResponse = {
  meta_types: Array<string>;
  meta_unit_types: Array<string>;
  available: boolean;
  amenities: Array<string>;
  range_available_price: Array<string | number>;
  region_center: Array<string | number>;
  meta_min_lease_duration: string;
  meta_max_lease_duration: string;
  move_in_month: string;
  university: string;
  locality: string;
  guarantor: string;
  tags: Array<string>;
  sort_key: string;
  housing_type: string;
  bills_included: Array<string>;
  amber_sale_status: string;
  //   filterCount: {
  //     totalCount: number;
  //     data: {
  //       meta_unit_types: {
  //         student_accommodation: number;
  //         independent_house: number;
  //       };
  //       lease_duration: {
  //         "0.0-25.0": number;
  //         "26.0-40.0": number;
  //         "41.0-45.0": number;
  //         "46.0-*": number;
  //       };
  //       meta_types: {
  //         private_room: number;
  //         private_bathroom: number;
  //         private_kitchen: number;
  //         shared_kitchen: number;
  //         entire_place: number;
  //         shared_bathroom: number;
  //         shared_room: number;
  //       };
  //     };
  //   };
};

export const defaultFiltersState: FiltersAPIResponse = {
  meta_types: [],
  meta_unit_types: [],
  available: false,
  amenities: [],
  range_available_price: [],
  region_center: ["", "", MAX_DISTANCE_VALUE],
  meta_min_lease_duration: "",
  meta_max_lease_duration: "",
  move_in_month: "",
  university: "",
  locality: "",
  guarantor: "",
  tags: [],
  sort_key: "relevance_sorting_v2,desc",
  housing_type: "all",
  bills_included: [],
  amber_sale_status: "",
  //   filterCount: {
  //     totalCount: 0,
  //     data: {
  //       meta_unit_types: {
  //         student_accommodation: 0,
  //         independent_house: 0,
  //       },
  //       lease_duration: {
  //         "0.0-25.0": 0,
  //         "26.0-40.0": 0,
  //         "41.0-45.0": 0,
  //         "46.0-*": 0,
  //       },
  //       meta_types: {
  //         private_room: 0,
  //         private_bathroom: 0,
  //         private_kitchen: 0,
  //         shared_kitchen: 0,
  //         entire_place: 0,
  //         shared_bathroom: 0,
  //         shared_room: 0,
  //       },
  //     },
  //   },
};

export const filtersMap = {
  university: "university",
  locality: "locality",
  meta_types: "roomType",
  meta_unit_types: "accommodationType",
  available: "available",
  move_in_month: "moveInMonth",
  meta_min_lease_duration: "minLeaseDuration",
  meta_max_lease_duration: "maxLeaseDuration",
  tags: "dualOccupancy",
  range_available_price: "budget",
  sort_key: "sortKey",
  sort_order: "sortOrder",
  leaseDuration: "leaseDuration",
  guarantor: "guarantor",
  region_radius: "region_radius",
  housing_type: "housingType",
  amenities: "amenities",
  bills_included: "billsIncluded",
  amber_sale_status: "amberSaleStatus",
} as const;

export const reverseFilterMap = Object.keys(filtersMap).reduce((acc: any, key: any) => {
  // @ts-ignore
  acc[filtersMap[key]] = key;
  return acc;
}, {});

export type FiltersKeyAPIType = keyof typeof filtersMap;
export type FiltersKeyType = (typeof filtersMap)[FiltersKeyAPIType];

export type FilterState = {
  [k in FiltersKeyType]?: string | string[];
};

const formatCurrentFilterState = (currFilter: FiltersKeyType, value?: string) => {
  // @ts-ignore
  const defaultValue = defaultFiltersState[reverseFilterMap[currFilter]];

  if (
    currFilter === "roomType" ||
    currFilter === "accommodationType" ||
    currFilter === "budget" ||
    currFilter === "amenities" ||
    currFilter === "billsIncluded"
  ) {
    if (Array.isArray(value)) {
      return value;
    }
    return typeof value === "string" ? value?.split(",")?.filter(Boolean) : defaultValue;
  }

  return value || defaultValue;
};

const transformCurrentFilterState = (filterState: FilterState) => {
  const updatedState = {
    ...filterState,
    sortKey: [filterState.sortKey, filterState.sortOrder].join(","),
  };

  delete updatedState.sortOrder;

  return updatedState;
};

export const convertToFilterObj = (filter: any) =>
  Object.keys(filter).reduce((acc: any, it) => {
    if (it === "university") return acc;
    // Preserve locality=near-by-{timestamp}, but skip other locality values when university is not present
    // Use includes to match the timestamp pattern
    if (
      it === "locality" &&
      !String(filter[it]).includes("near-by") &&
      !Object.keys(filter).includes("university")
    )
      return acc;
    if (it === "sortOrder") return acc;
    if (it === "sortKey") {
      const [sort_key, sort_order] = filter[it]?.split(",");
      return {
        ...acc,
        sort_key,
        sort_order,
      };
    }

    if (it === "minLeaseDuration" && filter[it] === "all_selected") {
      acc[reverseFilterMap[it]] = "";
      return acc;
    }

    acc[reverseFilterMap[it]] = Array.isArray(filter[it])
      ? filter[it]?.join(",")?.toLowerCase()
      : filter[it]?.toLowerCase();
    return acc;
  }, {});

const formatFilterState = (filterState: {
  university?: string;
  locality?: string;
  range_available_price?: string;
  meta_types?: string;
  meta_unit_types?: string;
  available?: string;
  meta_min_lease_duration?: string;
  meta_max_lease_duration?: string;
  move_in_month?: string;
  tags?: string;
  sort_key?: string;
  sort_order?: string;
  guarantor?: string;
  housing_type?: string;
  amenities?: string;
  bills_included?: string;
  amber_sale_status?: string;
}) => {
  const formattedFilterState: FilterState = Object.keys(filterState).reduce(
    (filterAcc: Record<string, string[] | string>, currFilterKey: string) => {
      //   @ts-ignore
      const currentFilter = filtersMap[currFilterKey];

      return currentFilter
        ? {
            ...filterAcc,
            //   @ts-ignore
            [currentFilter]: formatCurrentFilterState(currentFilter, filterState[currFilterKey]),
          }
        : { ...filterAcc };
    },
    { sortKey: "relevance_sorting_v2", sortOrder: "desc" }, // TODO Abhishek: Move default state here
  );

  const transformedFilterState = transformCurrentFilterState(formattedFilterState);

  return transformedFilterState;
};

export default formatFilterState;
